import { makeAutoObservable, runInAction } from 'mobx';
import { agent } from '../api/agent';
import { toast } from 'react-toastify';
import i18n from '../../i18n';
import { AddGameVideoLinkDTO, GameVideoLink } from '../models/GameVideoLink';

export default class GameVideoLinkStore {
    videoLinks: GameVideoLink[] = [];
    loading     = false;   // list / initial load
    submitting  = false;   // add form submission
    deletingId: string | null = null; // id of link being deleted

    constructor() {
        makeAutoObservable(this);
    }

    // ── List ──────────────────────────────────────────────────────────────────
    loadVideoLinks = async (gameId: string) => {
        this.loading = true;
        try {
            const links = await agent.GameVideoLinks.list(gameId);
            runInAction(() => {
                this.videoLinks = links;
                this.loading    = false;
            });
        } catch (error) {
            console.error('loadVideoLinks error:', error);
            runInAction(() => { this.loading = false; });
            toast.error((i18n as any).t('gameVideoLinks.errors.loadFailed'));
        }
    };

    // ── Add ───────────────────────────────────────────────────────────────────
    addVideoLink = async (dto: AddGameVideoLinkDTO): Promise<boolean> => {
        this.submitting = true;
        try {
            const created = await agent.GameVideoLinks.add(dto);
            runInAction(() => {
                this.videoLinks.unshift(created); // newest first
                this.submitting = false;
            });
            toast.success((i18n as any).t('gameVideoLinks.success.added'));
            return true;
        } catch (error) {
            console.error('addVideoLink error:', error);
            runInAction(() => { this.submitting = false; });
            toast.error((i18n as any).t('gameVideoLinks.errors.addFailed'));
            return false;
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    deleteVideoLink = async (videoLinkId: string): Promise<boolean> => {
        this.deletingId = videoLinkId;
        try {
            await agent.GameVideoLinks.delete(videoLinkId);
            runInAction(() => {
                this.videoLinks = this.videoLinks.filter(v => v.id !== videoLinkId);
                this.deletingId = null;
            });
            toast.success((i18n as any).t('gameVideoLinks.success.deleted'));
            return true;
        } catch (error) {
            console.error('deleteVideoLink error:', error);
            runInAction(() => { this.deletingId = null; });
            toast.error((i18n as any).t('gameVideoLinks.errors.deleteFailed'));
            return false;
        }
    };

    // ── Cleanup ───────────────────────────────────────────────────────────────
    clearVideoLinks = () => {
        this.videoLinks = [];
        this.loading    = false;
        this.submitting = false;
        this.deletingId = null;
    };
}